import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Pencil, X, Lock, Eye, Copy, Check, Trash2 } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import DateFilter, { DateInput, formatDisplayDate, isTodayFilter, allDateFilter } from './DateFilter';
import { usePagedQuery, dateRangeParams } from './usePagedQuery';
import { Pagination, SearchBox, DebouncedInput } from './TableControls';
import ExportButtons from './ExportButtons';
import { sameText, uniqueNormalized, spocsFromCompanies } from '../utils/filterUtils';

const emptyForm = {
    req_id: '',
    date_received: '',
    date_close: '',
    company_name: '',
    client_spoc: '',
    evanti_spoc: '',
    action_date: '',
    job_role: '',
    role_type: '',
    bill_rate: '',
    location: '',
    experience: '',
    candidate_budget: '',
    client_budget: '',
    positions: 1,
    status: 'Open',
    job_description: ''
};

const ROLE_TYPE_OPTIONS = ['Full Time', 'Sub-Con', 'Project'];

const JOB_STATUS_OPTIONS = ['Open', 'Slow Moving Profile', 'Hold by Evanti', 'Hold By Client', 'Closed by Client', 'Closed by Evanti'];

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none'
};

const lockedInputStyle = {
    ...inputStyle,
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed'
};

const textareaStyle = { ...inputStyle, resize: 'vertical' };

const labelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const fieldWrapStyle = { display: 'flex', flexDirection: 'column' };

const statusColors = {
    'Open': { background: '#dbeafe', color: '#1e40af' },
    'Slow Moving Profile': { background: '#dbeafe', color: '#1e40af' },
    'Hold by Evanti': { background: '#fef3c7', color: '#92400e' },
    'Hold By Client': { background: '#fef3c7', color: '#92400e' },
    'Closed by Client': { background: '#fee2e2', color: '#991b1b' },
    'Closed by Evanti': { background: '#dcfce7', color: '#166534' }
};

const detailRowStyle = { display: 'flex', flexDirection: 'column', gap: '2px' };
const detailLabelStyle = { fontSize: '11px', color: '#64748b', fontWeight: '700' };
const detailValueStyle = { fontSize: '13px', color: '#0f172a' };
const API = process.env.REACT_APP_BACKEND_API_URL;
const filterSelectStyle = { ...inputStyle, width: 'auto', minWidth: '150px' };

const JobGathering = ({ user, onLogout }) => {
    const isViewOnly = user?.role === 'recruiter';
    const isTrueAdmin = user?.role === 'admin';
    const { isMobile, isTablet } = useResponsive();

    const navigate = useNavigate();
    const [form, setForm] = useState(emptyForm);
    const [editingJob, setEditingJob] = useState(null); // the job being edited (the list is paged, so it may not be loaded in `jobs`)
    const [listSearch, setListSearch] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const isEditing = editingId !== null;
    // Admin edits everything except Job ID; Team Lead only the fields listed in the spec.
    const fieldLocked = isEditing && user?.role !== 'admin';
    const isAdmin = user?.role === 'admin' || user?.role === 'teamLead';
    const [viewingJob, setViewingJob] = useState(null);
    const [deleteConfirmJob, setDeleteConfirmJob] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [copiedJD, setCopiedJD] = useState(false);
    const [dateFilter, setDateFilter] = useState(allDateFilter);
    const [dateResetSignal, setDateResetSignal] = useState(0);
    // const [listDateFilter, setListDateFilter] = useState(todayDateFilter);
    // const [listActionDateFilter, setListActionDateFilter] = useState(todayDateFilter);
    // const [listDateResetSignal, setListDateResetSignal] = useState(0);
    const [listFilterJobId, setListFilterJobId] = useState('');
    const [listFilterReqId, setListFilterReqId] = useState('');
    const [listFilterCompany, setListFilterCompany] = useState('');
    const [listFilterJobRole, setListFilterJobRole] = useState('');
    const [listFilterRoleType, setListFilterRoleType] = useState('');
    const [listFilterStatus, setListFilterStatus] = useState('');
    const [listFilterClientSpoc, setListFilterClientSpoc] = useState('');
    const [listFilterEvantiSpoc, setListFilterEvantiSpoc] = useState('');

    const [clientSpocMode, setClientSpocMode] = useState('select');
    const [evantiSpocMode, setEvantiSpocMode] = useState('select');

    // Company Master — the single source of truth. Job Gathering only ever
    // selects from it now; company creation happens exclusively in the
    // Admin-only Company Details tab.
    // /api/companies returns every company WITH its SPOCs, so this one response feeds both
    // the Company dropdown and the Client SPOC dropdown.
    const [companyMasterList, setCompanyMasterList] = useState([]);
    const fetchCompanyMaster = useCallback(() => {
        fetch(`${API}/api/companies`)
            .then(res => res.json())
            .then(data => setCompanyMasterList(Array.isArray(data) ? data : []))
            .catch(err => console.error('Failed to fetch companies:', err));
    }, []);
    useEffect(() => { fetchCompanyMaster(); }, [fetchCompanyMaster]);

    // Client SPOC options = the company's SPOCs from the Company Master (so a SPOC added in
    // Company Details is selectable immediately, even for a company with no jobs yet), plus any
    // already on that company's jobs. De-duplicated case/whitespace-insensitively.

    // Option lists come from the API (distinct values over ALL job requirements), not from the current page.
    const [jobOptions, setJobOptions] = useState({});
    const [formCompanyOptions, setFormCompanyOptions] = useState({});
    const [listCompanyOptionsData, setListCompanyOptionsData] = useState({});
    const [optionsTick, setOptionsTick] = useState(0);
    const loadOptions = useCallback((company, setter) => {
        const qs = company ? `?company=${encodeURIComponent(company)}` : '';
        fetch(`${API}/api/job-requirements/filter-options${qs}`)
            .then(res => res.json())
            .then(data => setter(data && !data.error ? data : {}))
            .catch(err => console.error('Failed to fetch job filter options:', err));
    }, []);
    useEffect(() => { loadOptions('', setJobOptions); }, [loadOptions, optionsTick]);
    useEffect(() => { if (form.company_name) loadOptions(form.company_name, setFormCompanyOptions); else setFormCompanyOptions({}); }, [form.company_name, loadOptions, optionsTick]);
    useEffect(() => { if (listFilterCompany) loadOptions(listFilterCompany, setListCompanyOptionsData); else setListCompanyOptionsData({}); }, [listFilterCompany, loadOptions, optionsTick]);

    const companyOptions = jobOptions.company_name || [];
    const clientSpocOptions = uniqueNormalized(
        spocsFromCompanies(companyMasterList, form.company_name),
        formCompanyOptions.client_spoc || []
    );
    // Keep the job's own current SPOC selectable/selected even if it is not (yet) in the list,
    // and match it to the list's spelling so the <select> shows it as chosen.
    const clientSpocSelectValue = clientSpocOptions.find(o => sameText(o, form.client_spoc)) || form.client_spoc;
    const clientSpocChoices = form.client_spoc && !clientSpocOptions.some(o => sameText(o, form.client_spoc))
        ? [...clientSpocOptions, form.client_spoc]
        : clientSpocOptions;
    const evantiSpocOptions = jobOptions.evanti_spoc || [];
    const jobRoleOptions = jobOptions.job_role || [];

    const listCompanyOptions = companyOptions;
    const listJobRoleOptions = jobRoleOptions;
    const listClientSpocOptions = uniqueNormalized(
        spocsFromCompanies(companyMasterList, listFilterCompany),
        listFilterCompany ? (listCompanyOptionsData.client_spoc || []) : (jobOptions.client_spoc || [])
    );
    const listEvantiSpocOptions = evantiSpocOptions;

    // Server-side paging: newest job requirement first; every list filter is applied by the API.
    const listParams = useMemo(() => ({
        search: listSearch.trim(),
        job_id: listFilterJobId.trim(), req_id: listFilterReqId.trim(),
        company: listFilterCompany, job_role: listFilterJobRole, role_type: listFilterRoleType, status: listFilterStatus,
        client_spoc: listFilterClientSpoc, evanti_spoc: listFilterEvantiSpoc,
        ...dateRangeParams(dateFilter, 'received')
    }), [listSearch, listFilterJobId, listFilterReqId, listFilterCompany, listFilterJobRole, listFilterRoleType, listFilterStatus, listFilterClientSpoc, listFilterEvantiSpoc, dateFilter]);
    const table = usePagedQuery(`${API}/api/job-requirements`, listParams);
    const filteredJobs = table.rows;

    const hasListFilters = listSearch.trim() || listFilterJobId || listFilterReqId || listFilterCompany || listFilterJobRole ||
        listFilterRoleType || listFilterStatus || listFilterClientSpoc || listFilterEvantiSpoc ||
        !isTodayFilter(dateFilter);

    const clearListFilters = () => {
        setListSearch(''); setListFilterJobId(''); setListFilterReqId(''); setListFilterCompany('');
        setListFilterJobRole(''); setListFilterRoleType(''); setListFilterStatus('');
        setListFilterClientSpoc(''); setListFilterEvantiSpoc('');
        setDateFilter(allDateFilter()); setDateResetSignal(x => x + 1);
    };

    const fetchJobs = useCallback(() => {
        table.refetch();
        setOptionsTick(t => t + 1);
    }, [table.refetch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeleteJob = async () => {
        if (!deleteConfirmJob) return;
        setDeleting(true);
        try {
            const response = await fetch(
                `${API}/api/job-requirements/${deleteConfirmJob.id}?requester_role=${encodeURIComponent(user?.role || '')}`,
                { method: 'DELETE' }
            );
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setError(data.error || 'Failed to delete job requirement.');
            } else {
                fetchJobs();
            }
        } catch (err) {
            console.error('Failed to delete job requirement:', err);
            setError('Server connection failed.');
        } finally {
            setDeleting(false);
            setDeleteConfirmJob(null);
        }
    };

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleCompanyChange = (e) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, company_name: value, client_spoc: '' }));
        setClientSpocMode('select');
    };

    const startEdit = (job) => {
        setEditingId(job.id);
        setEditingJob(job);
        setForm({
            req_id: job.req_id || '',
            date_received: job.date_received?.split('T')[0] || '',
            date_close: job.date_close?.split('T')[0] || '',
            company_name: job.company_name || '',
            client_spoc: job.client_spoc || '',
            evanti_spoc: job.evanti_spoc || '',
            action_date: job.action_date?.split('T')[0] || '',
            job_role: job.job_role || '',
            role_type: job.role_type || '',
            bill_rate: job.bill_rate || '',
            location: job.location || '',
            experience: job.experience || '',
            candidate_budget: job.candidate_budget || '',
            client_budget: job.client_budget || '',
            positions: job.positions || 1,
            status: job.status || 'Open',
            job_description: job.job_description || ''
        });
        setClientSpocMode('select');
        setEvantiSpocMode('select');
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setClientSpocMode('select');
        setEvantiSpocMode('select');
        setEditingId(null);
        setForm(emptyForm);
        setError('');
    };

    const handleCopyJD = async () => {
        if (!viewingJob?.job_description) return;
        try {
            await navigator.clipboard.writeText(viewingJob.job_description);
            setCopiedJD(true);
            setTimeout(() => setCopiedJD(false), 2000);
        } catch (err) {
            console.error('Failed to copy job description:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!fieldLocked) {
            const missing = [];
            if (!form.date_received) missing.push('Date Received');
            if (!form.company_name) missing.push('Company Name');
            if (!form.role_type) missing.push('Role Type');
            if (!form.job_role) missing.push('Job Role');
            if (!form.experience) missing.push('Experience');
            if (!form.location) missing.push('Location');
            if (!form.candidate_budget) missing.push('Candidate Budget');
            if (!form.evanti_spoc) missing.push('Evanti SPOC');
            if (missing.length > 0) {
                setError(`Missing required field(s): ${missing.join(', ')}`);
                return;
            }
        }

        setSaving(true);
        try {
            const url = isEditing
                ? `${API}/api/job-requirements/${editingId}`
                : `${API}/api/job-requirements`;
            const method = isEditing ? 'PATCH' : 'POST';

            const payload = isEditing
                ? {
                    date_close: form.date_close,
                    action_date: form.action_date,
                    status: form.status,
                    job_description: form.job_description,
                    req_id: form.req_id,
                    bill_rate: form.bill_rate,
                    client_spoc: form.client_spoc,
                    client_budget: form.client_budget,
                    requester_role: user?.role,
                    ...(user?.role === 'admin' ? {
                        date_received: form.date_received, company_name: form.company_name, evanti_spoc: form.evanti_spoc,
                        job_role: form.job_role, role_type: form.role_type, location: form.location,
                        experience: form.experience, candidate_budget: form.candidate_budget, positions: form.positions
                    } : {})
                }
                : { ...form, requester_role: user?.role };

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Failed to save job requirement.');
                setSaving(false);
                return;
            }

            setForm(emptyForm);
            setEditingId(null);
            setClientSpocMode('select');
            setEvantiSpocMode('select');
            if (!isEditing) table.setPage(1);
            fetchJobs();
            fetchCompanyMaster(); // a save can introduce a new SPOC (the backend registers it in the master)
        } catch (err) {
            console.error('Failed to save job requirement:', err);
            setError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Job Gathering" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate(user?.role === 'recruiter' ? '/recruiter' : user?.role === 'admin' ? '/admin' : '/team-lead')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', background: '#fff',
                        border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px',
                        fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer'
                    }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Job Gathering</h2>
            </div>

            {!isViewOnly && (
                <form
                    onSubmit={handleSubmit}
                    style={{
                        background: '#fff', borderRadius: '12px',
                        boxShadow: isEditing ? '0 0 0 2px #2563eb' : '0 1px 3px rgba(0,0,0,0.05)',
                        padding: '24px', marginBottom: '24px', boxSizing: 'border-box'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                            {isEditing ? `Editing Job Requirement — ${editingJob?.job_id || ''}` : 'Add New Job Requirement'}
                        </h3>
                        {isEditing && (
                            <button
                                type="button"
                                onClick={cancelEdit}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '4px', background: '#f1f5f9',
                                    color: '#475569', border: 'none', padding: '6px 12px', borderRadius: '6px',
                                    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                                }}
                            >
                                <X size={13} /> Cancel Edit
                            </button>
                        )}
                    </div>

                    {error && (
                        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
                            {error}
                        </div>
                    )}

                    {isEditing && (
                        <div style={{ background: '#eff6ff', color: '#1e40af', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '16px' }}>
                            Job ID <strong>{editingJob?.job_id}</strong> — auto-generated, cannot be changed.
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Req ID</label>
                            <input
                                style={inputStyle}
                                value={form.req_id}
                                onChange={handleChange('req_id')}
                                placeholder="Optional — e.g. REQ-4521"
                            />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Date Received {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>
                            <DateInput
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.date_received}
                                onChange={handleChange('date_received')}
                                required={!fieldLocked}
                                readOnly={fieldLocked}
                                disabled={fieldLocked}
                            />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Date Close</label>
                            <DateInput style={inputStyle} value={form.date_close} onChange={handleChange('date_close')} />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Action Date</label>
                            <DateInput style={inputStyle} value={form.action_date} onChange={handleChange('action_date')} />
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Company Name {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>

                            <select
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.company_name}
                                onChange={handleCompanyChange}
                                required
                                disabled={fieldLocked}
                            >
                                <option value="">
                                    {companyMasterList.length
                                        ? 'Select company...'
                                        : 'No companies yet — add one in Company Details'}
                                </option>

                                {companyMasterList.map(c => (
                                    <option key={c.id} value={c.short_name}>
                                        {c.short_name}
                                    </option>
                                ))}
                            </select>

                            <span style={{
                                fontSize: '10px',
                                color: '#94a3b8',
                                marginTop: '3px'
                            }}>
                                Companies are managed in Company Details (Admin only).
                            </span>
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Client SPOC</label>
                            {clientSpocMode === 'select' ? (
                                <select
                                    style={inputStyle}
                                    value={clientSpocSelectValue}
                                    onChange={(e) => {
                                        if (e.target.value === '__add_new__') {
                                            setClientSpocMode('custom');
                                            setForm(prev => ({ ...prev, client_spoc: '' }));
                                        } else {
                                            setForm(prev => ({ ...prev, client_spoc: e.target.value }));
                                        }
                                    }}
                                    disabled={!form.company_name}
                                >
                                    <option value="">
                                        {form.company_name ? 'Select SPOC...' : 'Select a company first'}
                                    </option>
                                    {clientSpocChoices.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                    {isAdmin && form.company_name && <option value="__add_new__">+ Add New SPOC</option>}
                                </select>
                            ) : (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        style={{ ...inputStyle, flex: 1 }}
                                        value={form.client_spoc}
                                        onChange={(e) => setForm(prev => ({ ...prev, client_spoc: e.target.value }))}
                                        placeholder="New SPOC name"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => { setClientSpocMode('select'); setForm(prev => ({ ...prev, client_spoc: '' })); }}
                                        style={{ padding: '0 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}
                                    >
                                        Choose existing
                                    </button>
                                </div>
                            )}
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Evanti SPOC {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>
                            {evantiSpocMode === 'select' ? (
                                <select
                                    style={fieldLocked ? lockedInputStyle : inputStyle}
                                    value={form.evanti_spoc}
                                    onChange={(e) => {
                                        if (e.target.value === '__add_new__') {
                                            setEvantiSpocMode('custom');
                                            setForm(prev => ({ ...prev, evanti_spoc: '' }));
                                        } else {
                                            setForm(prev => ({ ...prev, evanti_spoc: e.target.value }));
                                        }
                                    }}
                                    required={!fieldLocked}
                                    disabled={fieldLocked}
                                >
                                    <option value="">Select Evanti SPOC...</option>
                                    {evantiSpocOptions.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                    {!fieldLocked && (
                                        <option value="__add_new__">
                                            + Add New Evanti SPOC
                                        </option>
                                    )}
                                </select>
                            ) : (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        style={{ ...inputStyle, flex: 1 }}
                                        value={form.evanti_spoc}
                                        onChange={(e) => setForm(prev => ({ ...prev, evanti_spoc: e.target.value }))}
                                        placeholder="New Evanti SPOC name"
                                        autoFocus
                                        required
                                    />
                                    {evantiSpocOptions.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => { setEvantiSpocMode('select'); setForm(prev => ({ ...prev, evanti_spoc: '' })); }}
                                            style={{ padding: '0 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}
                                        >
                                            Choose existing
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Job Role {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>
                            <input
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.job_role}
                                onChange={handleChange('job_role')}
                                placeholder="Senior React Developer"
                                required={!fieldLocked}
                                disabled={fieldLocked}
                            />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Role Type {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>
                            <select
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.role_type}
                                onChange={handleChange('role_type')}
                                disabled={fieldLocked}
                                required={!fieldLocked}
                            >
                                <option value="">Select role type</option>
                                {ROLE_TYPE_OPTIONS.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                            </select>
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Evanti Pay / Bill Rate</label>
                            <input style={inputStyle} value={form.bill_rate} onChange={handleChange('bill_rate')} placeholder="e.g. ₹850/hr" />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Location {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>

                            <input
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.location}
                                onChange={handleChange('location')}
                                placeholder="Remote / City"
                                disabled={fieldLocked}
                                required={!fieldLocked} />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Experience {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>

                            <input
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.experience}
                                onChange={handleChange('experience')}
                                placeholder="3-5 years"
                                disabled={fieldLocked}
                                required={!fieldLocked}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                Candidate Budget {fieldLocked ? <Lock size={11} /> : '*'}
                            </label>

                            <input
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                value={form.candidate_budget}
                                onChange={handleChange('candidate_budget')}
                                placeholder="₹10-14 LPA"
                                disabled={fieldLocked}
                                required={!fieldLocked}
                            />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Client Budget</label>
                            <input style={inputStyle} value={form.client_budget} onChange={handleChange('client_budget')} placeholder="e.g. ₹15 LPA ceiling" />
                        </div>

                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                                No. of Positions {fieldLocked && <Lock size={11} />}
                            </label>

                            <input
                                style={fieldLocked ? lockedInputStyle : inputStyle}
                                type="number"
                                min="1"
                                value={form.positions}
                                onChange={handleChange('positions')}
                                disabled={fieldLocked}
                            />
                        </div>
                        <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Status</label>
                            <select style={inputStyle} value={form.status} onChange={handleChange('status')}>
                                {JOB_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ ...fieldWrapStyle, gridColumn: isMobile ? 'span 1' : 'span 2', marginBottom: '16px' }}>
                        <label style={labelStyle}>Job Description</label>
                        <textarea
                            style={{ ...textareaStyle, minHeight: '42px' }}
                            rows={1}
                            value={form.job_description}
                            onChange={handleChange('job_description')}
                            placeholder="Full job description, responsibilities, requirements..."
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: isEditing ? '#f59e0b' : '#2563eb', color: '#fff',
                            border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px',
                            fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
                        }}
                    >
                        <Save size={14} /> {saving ? 'Saving...' : isEditing ? 'Update Job Requirement' : 'Save Job Requirement'}
                    </button>
                </form>
            )}

            {/* Received Date and Action Date, side by side (wrapping on narrow screens) */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>Date Received</div>
                    <DateFilter key={`act-${dateResetSignal}`} dateField="date_received" initialValue={dateFilter} onFilterChange={setDateFilter} />
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px 20px', marginTop: '12px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                <SearchBox value={listSearch} onChange={setListSearch} placeholder="Search all fields..." minWidth="200px" />
                <DebouncedInput
                    style={{ ...filterSelectStyle, minWidth: '130px' }}
                    placeholder="Search Job ID..."
                    value={listFilterJobId}
                    onChange={setListFilterJobId}
                />
                <DebouncedInput
                    style={{ ...filterSelectStyle, minWidth: '130px' }}
                    placeholder="Search Req ID..."
                    value={listFilterReqId}
                    onChange={setListFilterReqId}
                />
                <select
                    style={filterSelectStyle}
                    value={listFilterCompany}
                    onChange={(e) => { setListFilterCompany(e.target.value); setListFilterClientSpoc(''); }}
                >
                    <option value="">All Companies</option>
                    {listCompanyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                    style={filterSelectStyle}
                    value={listFilterJobRole}
                    onChange={(e) => setListFilterJobRole(e.target.value)}
                >
                    <option value="">All Job Roles</option>
                    {listJobRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                    style={filterSelectStyle}
                    value={listFilterRoleType}
                    onChange={(e) => setListFilterRoleType(e.target.value)}
                >
                    <option value="">All Role Types</option>
                    {ROLE_TYPE_OPTIONS.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                </select>
                <select
                    style={filterSelectStyle}
                    value={listFilterStatus}
                    onChange={(e) => setListFilterStatus(e.target.value)}
                >
                    <option value="">All Statuses</option>
                    {JOB_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    style={filterSelectStyle}
                    value={listFilterClientSpoc}
                    onChange={(e) => setListFilterClientSpoc(e.target.value)}
                >
                    <option value="">All Client SPOCs</option>
                    {listClientSpocOptions.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <select
                    style={filterSelectStyle}
                    value={listFilterEvantiSpoc}
                    onChange={(e) => setListFilterEvantiSpoc(e.target.value)}
                >
                    <option value="">All Evanti SPOCs</option>
                    {listEvantiSpocOptions.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                {hasListFilters && (
                    <button
                        onClick={clearListFilters}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                    >
                        <X size={13} /> Clear Filters
                    </button>
                )}
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '20px', overflowX: 'auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                        Job Requirements Tracker ({table.total.toLocaleString()})
                    </h3>
                    <ExportButtons
                        getRows={table.fetchAll}
                        columns={[
                            { key: 'job_id', label: 'Job ID' }, { key: 'req_id', label: 'Req ID' },
                            { key: 'date_received', label: 'Date Received' },
                            { key: 'date_close', label: 'Date Close' }, { key: 'action_date', label: 'Action Date' },
                            { key: 'company_name', label: 'Company Name' }, { key: 'client_spoc', label: 'Client SPOC' },
                            { key: 'evanti_spoc', label: 'Evanti SPOC' }, { key: 'job_role', label: 'Job Role' },
                            { key: 'role_type', label: 'Role Type' }, { key: 'bill_rate', label: 'Evanti Pay / Bill Rate' },
                            { key: 'location', label: 'Location' }, { key: 'experience', label: 'Experience' },
                            { key: 'candidate_budget', label: 'Candidate Budget' }, { key: 'client_budget', label: 'Client Budget' },
                            { key: 'positions', label: 'No. of Positions' },
                            { key: 'status', label: 'Status' }, { key: 'job_description', label: 'Job Description' }
                        ]}
                        filename="job-gathering"
                        title="Job Requirements Tracker"
                    />
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '10px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                            {(isViewOnly
                                ? ['Job ID', 'Date Received', 'Action Date', 'Company Name', 'Job Role', 'Role Type', 'Status', 'View Details']
                                : isTrueAdmin
                                    ? ['Job ID', 'Date Received', 'Action Date', 'Company Name', 'Job Role', 'Role Type', 'Status', 'View Details', 'Edit Details', 'Delete']
                                    : ['Job ID', 'Date Received', 'Action Date', 'Company Name', 'Job Role', 'Role Type', 'Status', 'View Details', 'Edit Details']
                            ).map(h => (
                                <th key={h} style={{
                                    padding: '10px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap',
                                    ...(h === 'Job ID' ? { width: '70px', minWidth: '70px' } : {}),
                                    ...(h === 'Job Role' ? { width: '400px', minWidth: '400px' } : {}),
                                    ...(h === 'View' || h === 'Edit' ? { width: '30px', minWidth: '30px' } : {})
                                }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredJobs.map((job, snoIdx) => (
                            <tr key={job.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '10px', color: '#64748b', fontWeight: '600', textAlign: 'center' }}>{(table.page - 1) * table.pageSize + snoIdx + 1}</td>
                                <td style={{ padding: '10px', fontWeight: '700', color: '#2563eb', minWidth: '70px', whiteSpace: 'nowrap' }}>
                                    {job.job_id}
                                </td>
                                {/* <td style={{
                                    padding: '10px', width: '70px', minWidth: '70px',
                                    maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {job.req_id || '-'}
                                </td> */}
                                <td style={{ padding: '10px' }}>{formatDisplayDate(job.date_received)}</td>
                                <td style={{ padding: '10px' }}>{formatDisplayDate(job.action_date)}</td>
                                <td style={{ padding: '10px' }}>{job.company_name}</td>
                                <td style={{ padding: '10px' }}>{job.job_role}</td>
                                <td style={{ padding: '10px' }}>{job.role_type || '-'}</td>
                                <td style={{ padding: '10px' }}>
                                    <span style={{ ...(statusColors[job.status] || statusColors.Open), padding: '3px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
                                        {job.status}
                                    </span>
                                </td>
                                <td style={{ padding: '10px' }}>
                                    <button
                                        onClick={() => { setViewingJob(job); setCopiedJD(false); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '4px', background: '#eff6ff',
                                            color: '#1e40af', border: 'none', padding: '5px 10px', borderRadius: '4px',
                                            fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <Eye size={12} /> View Details
                                    </button>
                                </td>
                                {!isViewOnly && (
                                    <td style={{ padding: '10px' }}>
                                        <button
                                            onClick={() => startEdit(job)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '4px', background: '#f1f5f9',
                                                color: '#334155', border: 'none', padding: '5px 10px', borderRadius: '4px',
                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <Pencil size={12} /> Edit Details
                                        </button>
                                    </td>
                                )}
                                {isTrueAdmin && (
                                    <td style={{ padding: '10px' }}>
                                        <button
                                            onClick={() => setDeleteConfirmJob(job)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2',
                                                color: '#b91c1c', border: 'none', padding: '5px 10px', borderRadius: '4px',
                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {filteredJobs.length === 0 && (
                            <tr>
                                <td colSpan={isViewOnly ? 10 : isTrueAdmin ? 12 : 11} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>
                                    {hasListFilters ? 'No job requirements match these filters.' : 'No job requirements found for today. Change the date filters to see earlier ones.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                    onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="job requirements" />
            </div>

            {viewingJob && (
                <div
                    onClick={() => setViewingJob(null)}
                    style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', boxSizing: 'border-box' }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', position: 'relative', boxSizing: 'border-box' }}
                    >
                        <button
                            onClick={() => setViewingJob(null)}
                            style={{ position: 'absolute', top: '18px', right: '18px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}
                        >
                            <X size={20} />
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>{viewingJob.job_id}</h3>
                            {viewingJob.req_id && (
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px', fontWeight: '600' }}>
                                    Req ID: {viewingJob.req_id}
                                </span>
                            )}
                            <span style={{
                                ...(statusColors[viewingJob.status] || statusColors.Open),
                                padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700'
                            }}>
                                {viewingJob.status}
                            </span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Job Role</span>
                                <span style={detailValueStyle}>{viewingJob.job_role}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Company Name</span>
                                <span style={detailValueStyle}>{viewingJob.company_name}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Date Received</span>
                                <span style={detailValueStyle}>{formatDisplayDate(viewingJob.date_received)}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Date Close</span>
                                <span style={detailValueStyle}>{formatDisplayDate(viewingJob.date_close)}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Action Date</span>
                                <span style={detailValueStyle}>{formatDisplayDate(viewingJob.action_date)}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Location</span>
                                <span style={detailValueStyle}>{viewingJob.location || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Client SPOC</span>
                                <span style={detailValueStyle}>{viewingJob.client_spoc || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Evanti SPOC</span>
                                <span style={detailValueStyle}>{viewingJob.evanti_spoc || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Experience</span>
                                <span style={detailValueStyle}>{viewingJob.experience || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Role Type</span>
                                <span style={detailValueStyle}>{viewingJob.role_type || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Evanti Pay / Bill Rate</span>
                                <span style={detailValueStyle}>{viewingJob.bill_rate || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Candidate Budget</span>
                                <span style={detailValueStyle}>{viewingJob.candidate_budget || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>Client Budget</span>
                                <span style={detailValueStyle}>{viewingJob.client_budget || '-'}</span>
                            </div>
                            <div style={detailRowStyle}>
                                <span style={detailLabelStyle}>No. of Positions</span>
                                <span style={detailValueStyle}>{viewingJob.positions}</span>
                            </div>
                        </div>

                        <div style={detailRowStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={detailLabelStyle}>Job Description</span>
                                {viewingJob.job_description && (
                                    <button
                                        onClick={handleCopyJD}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            background: copiedJD ? '#dcfce7' : '#eff6ff',
                                            color: copiedJD ? '#166534' : '#1e40af',
                                            border: 'none', padding: '4px 10px', borderRadius: '4px',
                                            fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                                        }}
                                    >
                                        {copiedJD ? <Check size={12} /> : <Copy size={12} />}
                                        {copiedJD ? 'Copied!' : 'Copy JD'}
                                    </button>
                                )}
                            </div>
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#334155', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                                {viewingJob.job_description || 'No description provided.'}
                            </div>
                        </div>

                        <button
                            onClick={() => setViewingJob(null)}
                            style={{ width: '100%', background: '#007bff', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', marginTop: '20px' }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {deleteConfirmJob && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Delete Job Requirement?</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                            This will permanently delete <strong>{deleteConfirmJob.job_id}</strong> ({deleteConfirmJob.job_role} — {deleteConfirmJob.company_name}). This cannot be undone.
                        </p>
                        {error && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>{error}</div>
                        )}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => { setDeleteConfirmJob(null); setError(''); }}
                                disabled={deleting}
                                style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteJob}
                                disabled={deleting}
                                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobGathering;